import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useMembers } from "@/context/MemberContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Activity, Sparkles, ClipboardList, Stethoscope, Plus } from "lucide-react";
import MedAdherenceWidget from "@/components/MedAdherenceWidget";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmt(d) { return d.toISOString().slice(0, 10); }
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }

export default function Dashboard() {
  const navigate = useNavigate();
  const { activeId, active } = useMembers();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState({});
  const [details, setDetails] = useState({ vitals: [], symptoms: [], diagnostics: [], consultations: [] });

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  useEffect(() => {
    if (!activeId) return;
    let start, end;
    if (view === "year") {
      start = `${cursor.getFullYear()}-01-01`;
      end = `${cursor.getFullYear()}-12-31`;
    } else {
      start = fmt(monthStart);
      end = fmt(monthEnd);
    }
    api.get(`/calendar/events?start=${start}&end=${end}&member_id=${activeId}`).then(r => setEvents(r.data.events || {}));
  }, [cursor, view, activeId, monthStart, monthEnd]);

  useEffect(() => {
    if (!activeId) return;
    const d = fmt(selected);
    Promise.all([
      api.get(`/vitals?start=${d}&end=${d}&member_id=${activeId}`),
      api.get(`/symptoms?start=${d}&end=${d}&member_id=${activeId}`),
      api.get(`/diagnostics?start=${d}&end=${d}&member_id=${activeId}`),
      api.get(`/consultations?start=${d}&end=${d}&member_id=${activeId}`),
    ]).then(([v, s, dx, c]) => {
      setDetails({ vitals: v.data, symptoms: s.data, diagnostics: dx.data, consultations: c.data });
    }).catch(() => {});
  }, [selected, activeId]);

  const grid = useMemo(() => {
    const firstDay = monthStart.getDay();
    const daysInMonth = monthEnd.getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) {
      const d = new Date(monthStart);
      d.setDate(d.getDate() - (firstDay - i));
      cells.push({ date: d, otherMonth: true });
    }
    for (let i = 1; i <= daysInMonth; i++) cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), i), otherMonth: false });
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const d = new Date(last); d.setDate(d.getDate() + 1);
      cells.push({ date: d, otherMonth: true });
    }
    return cells;
  }, [monthStart, monthEnd, cursor]);

  const move = (delta) => {
    const d = new Date(cursor);
    if (view === "year") d.setFullYear(d.getFullYear() + delta);
    else if (view === "month") d.setMonth(d.getMonth() + delta);
    else d.setDate(d.getDate() + delta);
    setCursor(d);
    if (view === "day") setSelected(d);
  };

  const today = new Date();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Overview · {active?.name || "—"}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {view === "year" ? cursor.getFullYear() :
             view === "month" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}` :
             selected.toDateString()}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={view} onValueChange={setView} data-testid="view-tabs">
            <TabsList className="bg-[#F1F3F0]">
              <TabsTrigger value="day" data-testid="view-day">Day</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Month</TabsTrigger>
              <TabsTrigger value="year" data-testid="view-year">Year</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-1 bg-white border border-[#E5E7E4] rounded-xl p-1">
            <button data-testid="cal-prev" onClick={() => move(-1)} className="p-1.5 rounded-lg hover:bg-[#F1F3F0]"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-today" onClick={() => { setCursor(new Date()); setSelected(new Date()); }} className="px-2 py-1 text-sm">Today</button>
            <button data-testid="cal-next" onClick={() => move(1)} className="p-1.5 rounded-lg hover:bg-[#F1F3F0]"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {view === "month" && (
        <Card className="p-4 sm:p-6 rounded-2xl border border-[#E5E7E4]">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
              <div key={d} className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] text-center py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2" data-testid="month-grid">
            {grid.map(({ date, otherMonth }) => {
              const ds = fmt(date);
              const ev = events[ds];
              const isToday = sameDay(date, today);
              const isSel = sameDay(date, selected);
              return (
                <div key={`${ds}${otherMonth ? "-o" : ""}`} onClick={() => setSelected(date)} data-testid={`cal-day-${ds}`}
                  className={`cal-day ${otherMonth ? "is-other-month" : ""} ${isToday ? "is-today" : ""} ${isSel ? "is-selected" : ""}`}>
                  <span className="text-sm font-medium">{date.getDate()}</span>
                  {ev && (
                    <div className="flex flex-wrap gap-1 mt-auto">
                      {ev.vitals > 0 && <span className="dot dot-v" title="Vitals" />}
                      {ev.symptoms > 0 && <span className="dot dot-s" title="Symptoms" />}
                      {ev.diagnostics > 0 && <span className="dot dot-d" title="Diagnostics" />}
                      {ev.consultations > 0 && <span className="dot dot-c" title="Consultations" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-5 text-xs text-[#5C6D63]">
            <span className="flex items-center gap-1.5"><span className="dot dot-v" /> Vitals</span>
            <span className="flex items-center gap-1.5"><span className="dot dot-s" /> Symptoms</span>
            <span className="flex items-center gap-1.5"><span className="dot dot-d" /> Diagnostics</span>
            <span className="flex items-center gap-1.5"><span className="dot dot-c" /> Consultations</span>
          </div>
        </Card>
      )}

      {view === "year" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="year-grid">
          {MONTHS.map((m, idx) => {
            const monthEvents = Object.keys(events).filter(k => k.startsWith(`${cursor.getFullYear()}-${String(idx+1).padStart(2,"0")}`));
            const totals = monthEvents.reduce((acc, k) => {
              const e = events[k]; acc.v += e.vitals; acc.s += e.symptoms; acc.d += e.diagnostics; acc.c += e.consultations; return acc;
            }, {v:0,s:0,d:0,c:0});
            return (
              <Card key={m} className="p-5 rounded-2xl border border-[#E5E7E4] hover:shadow-md transition-all cursor-pointer"
                onClick={() => { setCursor(new Date(cursor.getFullYear(), idx, 1)); setView("month"); }}>
                <h3 className="font-heading font-medium text-lg">{m}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-[#5C6D63]">
                  <div className="flex justify-between"><span><span className="dot dot-v mr-2" />Vitals</span><span>{totals.v}</span></div>
                  <div className="flex justify-between"><span><span className="dot dot-s mr-2" />Symptoms</span><span>{totals.s}</span></div>
                  <div className="flex justify-between"><span><span className="dot dot-d mr-2" />Diagnostics</span><span>{totals.d}</span></div>
                  <div className="flex justify-between"><span><span className="dot dot-c mr-2" />Consultations</span><span>{totals.c}</span></div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {view === "day" && (
        <Card className="p-6 rounded-2xl border border-[#E5E7E4]">
          <h3 className="font-heading text-xl font-medium">{selected.toDateString()}</h3>
          <p className="text-sm text-[#5C6D63] mt-1">All entries for the day</p>
        </Card>
      )}

      {/* Detail panel for selected day */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DetailCard title="Vitals" icon={Activity} count={details.vitals.length} onAdd={() => navigate("/vitals")} color="#5B8266">
          {details.vitals.slice(0, 3).map((v, i) => (
            <div key={v.id} className="text-sm text-[#5C6D63]">
              {v.bp_systolic && <span>BP {v.bp_systolic}/{v.bp_diastolic} </span>}
              {v.sugar && <span>· Sugar {v.sugar} </span>}
              {v.heart_rate && <span>· HR {v.heart_rate}</span>}
            </div>
          ))}
        </DetailCard>
        <DetailCard title="Symptoms" icon={Sparkles} count={details.symptoms.length} onAdd={() => navigate("/symptoms")} color="#C76B58">
          {details.symptoms.slice(0, 3).map(s => <div key={s.id} className="text-sm text-[#5C6D63] truncate">• {s.symptom} ({s.severity})</div>)}
        </DetailCard>
        <DetailCard title="Diagnostics" icon={ClipboardList} count={details.diagnostics.length} onAdd={() => navigate("/diagnostics")} color="#5B7582">
          {details.diagnostics.slice(0, 3).map(d => <div key={d.id} className="text-sm text-[#5C6D63] truncate">• {d.type}</div>)}
        </DetailCard>
        <DetailCard title="Consultations" icon={Stethoscope} count={details.consultations.length} onAdd={() => navigate("/consultations")} color="#4A6750">
          {details.consultations.slice(0, 3).map(c => <div key={c.id} className="text-sm text-[#5C6D63] truncate">• Dr. {c.doctor_name}</div>)}
        </DetailCard>
      </div>

      {/* Today's medications adherence widget */}
      <MedAdherenceWidget />
    </div>
  );
}

function DetailCard({ title, icon: Icon, count, onAdd, color, children }) {
  return (
    <Card className="p-5 rounded-2xl border border-[#E5E7E4] bg-white hover:shadow-md transition-all" data-testid={`detail-${title.toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
            <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{title}</p>
            <p className="font-heading text-2xl font-semibold leading-tight mt-0.5">{count}</p>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onAdd} className="rounded-lg text-[#5C6D63] hover:text-[#1C2E24]">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="mt-3 space-y-1 min-h-[3rem]">
        {children && React.Children.count(children) > 0 ? children : <p className="text-xs text-[#7D8F84] italic">No entries</p>}
      </div>
    </Card>
  );
}
